import { Select } from "antd";
import { SelectProps } from "antd";
import classNames from "classnames";
import { FieldWrapper } from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { WithSaveButton } from "components/UI/Table/UI/InteractiveCell/components/WithSaveButton.tsx";
import { useInteractiveCell } from "components/UI/Table/UI/InteractiveCell/hooks/useInteractiveCell.ts";
import { AlwaysEditingFieldProps, FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import { ComponentProps } from "react";
import { FieldValues } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";

type Props<T extends FieldValues> = ComponentProps<typeof Select> & FieldProps<T> & AlwaysEditingFieldProps;

export const WithSelect = <T extends FieldValues = FieldValues>({ formControl, ...props }: Props<T>) => {
    const { onChange, onSave, value, className, alwaysEditing = false, ...restProps } = props;

    const {
        setControlledValue,
        handleOnSaveSideEffects,
        resolvedValue,
        getLatestValue,
        isFormControlled,
        handleFocus,
        handleBlur,
        fieldRef,
        error,
    } = useInteractiveCell({
        formControl,
        value: value as SelectProps["value"],
    });

    const handleChange: SelectProps["onChange"] = (value, option) => {
        setControlledValue(value);
        onChange?.(value, option);
    };

    const handleSave = () => {
        handleOnSaveSideEffects();
        onSave?.(getLatestValue());
    };

    const displayValue = props?.options?.find((option) => option.value === resolvedValue)?.label || resolvedValue;

    return (
        <FieldWrapper
            displayValue={displayValue || props.defaultValue}
            ref={fieldRef}
            isError={!!error}
            errorMessage={error?.message as string}
            isSelect={true}
            alwaysEditing={alwaysEditing}
        >
            <WithSaveButton onSave={handleSave} hideSaveButton={alwaysEditing}>
                {isFormControlled ? (
                    <FormItem {...(formControl as NonNullable<typeof formControl>)} noStyle>
                        <Select
                            {...restProps}
                            className={classNames(className, "!w-full")}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                        />
                    </FormItem>
                ) : (
                    <Select
                        {...restProps}
                        onChange={handleChange}
                        className={classNames(className, "!w-full")}
                        value={resolvedValue}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                    />
                )}
            </WithSaveButton>
        </FieldWrapper>
    );
};
